import {CheckCircle, DescriptionRounded, WarningRounded} from "@mui/icons-material";
import {Checkbox, Chip, Grid, IconButton, ListItemText, MenuItem, TextField, Tooltip} from "@mui/material";
import {useLocation, useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import SectionHeader from "../../../../util/component/SectionHeader";
import {AgGridReact} from "ag-grid-react";
import * as React from "react";
import {useContext, useEffect, useMemo, useRef, useState} from "react";
import {ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams} from "ag-grid-enterprise";
import {useAgGridLocale} from "../../../../config/globalConfig";
import AdvancedFilter from "../../component/AdvancedFilter";
import * as Yup from 'yup';
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import Stack from "@mui/material/Stack";
//import PoiDetailModal from "./modals/PoiDetailModal";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import CustomModal from "../../component/CustomModal";
import ContractDetailModal from "./modal/ContractDetailModal";
import {Contract, ContractFilters, ContractInputGetAll} from "./ContractInterfaces";
import {useStatusValuesContract} from "../../component/useStatusValuesContract";
import {useContractActions} from "../../service/useContractActions";
import {useContractStatuses} from "./hooks/useContractStatuses";
import ActionButtonWithTooltip, {ActionTypes} from "../../component/ActionButtonWithTooltip";
import EmptyState from "../../../../util/component/EmptyState";
import {DateTimePicker} from "@mui/x-date-pickers";
import {formatDate} from "../../component/Utils";


const ContractListPage = () => {
    const {globalData} = useContext(GlobalContext);

    const {t} = useTranslation(['salesmarketing'], {keyPrefix: 'contract'});
    const navigate = useNavigate();
    const {getContractList, getCategoriesBySegmentCode} = useContractActions()

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const customerIdExt = queryParams.get('customerId');
    const voucherCode = queryParams.get('voucherCode')

    const {getStatusColorContract,getStatusLabelContract} = useStatusValuesContract()
    const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
    const [openDetail, setOpenDetail] = useState(false);
    const filterDefaultValues : ContractFilters = {
        legalEntity: globalData?.user?.legalEntities?.map((le: any) => le.name),
        dealer: globalData?.user?.dealers?.map((d: any) => d.name),
        status:[],id:"",subscriptionPlanName:"",customerName:"", customerId: customerIdExt ? customerIdExt : "",commercialCategory:[],voucherCode:voucherCode ?? "",
        createdDateFrom: null, createdDateTo: null, lastModifiedDateFrom: null, lastModifiedDateTo: null
    };
    const customDefaultValues : ContractFilters = {
        legalEntity: globalData?.user?.legalEntities?.map((le: any) => le.name),
        dealer: globalData?.user?.dealers?.map((d: any) => d.name),
        status:[],id:"",subscriptionPlanName:"",customerName:"", customerId: "",commercialCategory:[], voucherCode:"",
        createdDateFrom: null, createdDateTo: null, lastModifiedDateFrom: null, lastModifiedDateTo: null
    };
    const [filters, setFilters] = useState<ContractFilters>(filterDefaultValues);
    const [searchInput, setSearchInput] = useState("");
    const [openUploadWarning, setOpenUploadWarning] = useState(false);
    const [uploadMessage, setUploadMessage] = useState<string>("");
    const [importError, setImportError] = useState(false);
    const {dispatchGlobalData} = useContext(GlobalContext);

    const agGridLocale = useAgGridLocale();
    const gridRef = useRef<AgGridReact>(null);
    const statuses = useContractStatuses();
    const [categories, setCategories] = useState<any[]>([]);

    const columns: ColDef[] = [
        {
            field: "tenantId",
            headerName: t('listColumns.legalEntityField'),
            hide: globalData?.user?.legalEntities?.length <= 1,
            unSortIcon: true,
            cellRenderer: (params: any) => {
                return globalData?.user?.legalEntities.find((le: any) => le.name == params.data.tenantId)?.name
            },
            tooltipValueGetter: (params: any) => {
                return globalData?.user?.legalEntities.find((le: any) => le.name == params.data.tenantId)?.name
            }
        },
        {
            field: "dealerId",
            headerName: t('listColumns.dealerField'),
            hide: globalData?.user?.dealers?.length <= 1,
            unSortIcon: true,
            
        },
        {
            field: "id",
            headerName: t('listColumns.contractId'),
            unSortIcon: true,
        },
        {
            field: "customerName",
            headerName: t('listColumns.customerName'),
            unSortIcon: true,
        },
        {
            field: "subscriptionPlanName",
            headerName: t('listColumns.subscription'),
            unSortIcon: true,
        },
        {
            field: "subscriptionPlanCategory",
            headerName: t('listColumns.commercialCategory'),
            unSortIcon: true,
        },
        {
            field: "acrisCode",
            headerName: t('listColumns.acriss'),
            unSortIcon: true,
        },
        {
            field: "creationDate",
            headerName: t('listColumns.creationDate'),
            unSortIcon: true,
            valueFormatter: (params: {data:Contract}) =>  {return params.data.creationDate ? formatDate(params.data.creationDate) : '--'}
        },
        {
            field: "lastModifiedDate",
            headerName: t('listColumns.lastModifiedDate'),
            unSortIcon: true,
            valueFormatter: (params: {data:Contract}) =>  {return params.data.lastModifiedDate ? formatDate(params.data.lastModifiedDate): '--'}
        },
        {
            field: "startDate",
            headerName: t('listColumns.contrStartdate'),
            unSortIcon: true,
            valueFormatter: (params: {data:Contract}) =>  {return params.data.startDate ? formatDate(params.data.startDate): '--'}
        },
        {
            field: "expirationDate",
            headerName: t('listColumns.expiration'),
            unSortIcon: true,
            valueFormatter: (params: {data:Contract}) =>  {return params.data.expirationDate ? formatDate(params.data.expirationDate)  : '--'}
        },
        {
            field: "status",
            maxWidth:160,
            headerName: t('listColumns.status'),
            pinned: 'right',
            lockPosition: 'right',
            lockPinned: true,
            cellClass: "grid-cell-center",
            headerClass: "grid-header-center",
            unSortIcon: true,
            cellRenderer: (params: {data:Contract}) => {
                return <Chip label={getStatusLabelContract(params.data.status.toUpperCase())}
                             sx={{backgroundColor: getStatusColorContract(params.data.status.toUpperCase())}}/>
            }
        },
        {
            field: "_actions",
            maxWidth: 160,
            headerName: t('listColumns.actions'),
            pinned: 'right',
            lockPosition: 'right',
            lockPinned: true,
            sortable: false,
            cellRenderer: (params: {data:Contract}) => {
                return (
                    <Stack direction={"row"} justifyContent={"center"}>
                        <ActionButtonWithTooltip type={ActionTypes.edit} callback={() => {
                            window.open(`/salesmarketing/contractUpdate/${params.data.id}`, "_blank");
                        }} auth={'SCOPE_SM_UPDATE_CONTRACTS'}/>
                        <ActionButtonWithTooltip type={ActionTypes.details} callback={() => {
                            setOpenDetail(true);
                            setSelectedContract(params.data);
                        }} auth={'SCOPE_SM_VIEW_CONTRACTS_DETAIL'}/>
                        <ActionButtonWithTooltip type={ActionTypes.kms} callback={() => {
                            window.open(`/salesmarketing/detectionlist/${params.data.id}`, "_blank");
                        }} auth={'SCOPE_SM_DETECTION'}/>
                    </Stack>
                );
            }
        }
    ];

    useEffect(() => {
        gridRef?.current?.api?.setServerSideDatasource(createDatasource(filters, gridRef.current!.api));
    }, [filters]);


    useEffect(() => {
        getCategoriesBySegmentCode().then((res: any) => {
            setCategories(res);
        })
    }, []);

    function createDatasource(filters:ContractFilters, api: GridApi) {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let filterTemp:ContractInputGetAll = {...filters};
                filterTemp.offset = params.request.startRow ? params.request.startRow : 0;
                filterTemp.limit = (params.request.endRow ? params.request.endRow : 10) - filterTemp.offset;
                filterTemp.sort = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                filterTemp.order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort;
                filterTemp.search = searchInput;
                try {
                 
                    let data:any = await getContractList(filterTemp, customerIdExt);
                    params.success({
                        rowData: data?.content,
                        rowCount: data?.totalElements
                    });
                    if (data.totalElements === 0) {
                        api.showNoRowsOverlay();
                    } else {
                        api.hideOverlay();
                    }
                } catch (e) {
                    params.fail();
                }
            }
        };
    }

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        suppressRowClickSelection: true,
        domLayout: 'autoHeight',
        rowModelType: 'serverSide',
        pagination: true,
        paginationPageSize: 10,
        cacheBlockSize: 10,
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: true
        },
        columnDefs: columns,
        serverSideInfiniteScroll: true,
        localeText: agGridLocale,
        noRowsOverlayComponentParams: {title: t('messages.noContractsDescription')}
    };
   
    const validationObj = {
        status:Yup.array().of(Yup.string()).nullable(),
        id:Yup.string().nullable(),
        createdDateFrom: Yup.date().nullable()
            .test("testCreatedDateFrom", t('filters.invalidDate'), (value, context) => {
                return (!value || !context.parent.createdDateTo || value <= context.parent.createdDateTo)
            })
            .typeError(t('filters.invalidDate')),
        createdDateTo: Yup.date().nullable()
            .test("testCreatedDateTo", t('filters.invalidDate'), (value, context) => {
                return (!value || !context.parent.createdDateFrom || value >= context.parent.createdDateFrom)
            })
            .typeError(t('filters.invalidDate')),
        lastModifiedDateFrom: Yup.date().nullable()
            .test("testLastModifiedDateFrom", t('filters.invalidDate'), (value, context) => {
                return (!value || !context.parent.lastModifiedDateTo || value <= context.parent.lastModifiedDateTo)
            })
            .typeError(t('filters.invalidDate')),
        lastModifiedDateTo: Yup.date().nullable()
            .test("testLastModifiedDateTo", t('filters.invalidDate'), (value, context) => {
                return (!value || !context.parent.lastModifiedDateFrom || value >= context.parent.lastModifiedDateFrom)
            })
            .typeError(t('filters.invalidDate')),
    }
    
    const renderStatus = (field: any) => (
        <TextField
            fullWidth
            label={t('filters.status')}
            select
            SelectProps={{
                multiple: true,
                renderValue: (selected: any) => selected.map((s: any) => statuses.find(ss => ss.value === s)!.label).join(', ')
            }}
            value={field.value}
            onChange={field.onChange}
        >
            {statuses?.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                    <Checkbox checked={field.value.indexOf(status.value) > -1}/>
                    <span style={{
                        border: "1px solid " + status.color,
                        backgroundColor: status.color,
                        borderRadius: "50%",
                        display: "inline-block",
                        height: "14px",
                        width: "14px",
                        marginRight: "5px"
                    }}/>
                    <ListItemText primary={status.label}/>
                </MenuItem>
            ))}
        </TextField>
    )


    const renderId = (field:any) => (
        <TextField
        fullWidth
        label={t('filters.id')}
        value={field.value}
        onChange={field.onChange}/>
    )

    const renderSubscriptionPlanName = (field:any) => (
        <TextField
        fullWidth
        label={t('filters.subscription')}
        value={field.value}
        onChange={field.onChange}/>
    )
        
    const renderCustomerName = (field:any) => (
        <TextField
        fullWidth
        label={t('filters.customerName')}
        value={field.value}
        onChange={field.onChange}/>
    )

    const renderCustomerId = (field:any) => ( 
        <TextField
        fullWidth
        label={t('filters.customerId')}
        value={field.value}
        onChange={field.onChange}/>
    )

    const renderCommercialCategory = (field:any) => (
        <TextField
            fullWidth
            label={t('filters.commercialCategory')}
            select
            SelectProps={{
                multiple: true,
                renderValue: (selected: any) => selected.join(', ')
            }}
            value={field.value}
            onChange={field.onChange}
        >
            {categories?.map((category) => (
                <MenuItem key={category.categoryCode} value={category.categoryName}>
                    <Checkbox checked={field.value.indexOf(category.categoryName) > -1}/>
                    <ListItemText primary={category.categoryName}/>
                </MenuItem>
            ))}
        </TextField>
    )

    const renderVoucherCode = (field:any) => (
        <TextField
        fullWidth
        label={t('filters.voucherCode')}
        value={field.value}
        onChange={field.onChange}/>
    )

    const renderCreatedDateFrom = (field: any, error: any, formState: any, values: any) => (
        <DateTimePicker
            label={t('filters.createdDateFrom')}
            value={field.value}
            maxDate={values["createdDateTo"] !== null ? values["createdDateTo"] : null}
            onChange={field.onChange}
            ampm={false}
            renderInput={(params) => (
                <TextField
                    {...params}
                    fullWidth
                    id={"createdDateFrom"}
                    error={error !== undefined}
                    helperText={error ? error.message : ""}
                />
            )}
            inputFormat={"DD/MM/YYYY HH:mm"}
        />
    )
    const renderCreatedDateTo = (field: any, error: any, formState: any, values: any) => (
        <DateTimePicker
            label={t('filters.createdDateTo')}
            value={field.value}
            minDate={values["createdDateFrom"] !== null ? values["createdDateFrom"] : null}
            onChange={field.onChange}
            ampm={false}
            renderInput={(params) => (
                <TextField
                    {...params}
                    fullWidth
                    id={"createdDateTo"}
                    error={error !== undefined}
                    helperText={error ? error.message : ""}
                />
            )}
            inputFormat={"DD/MM/YYYY HH:mm"}
        />
    )
    const renderLastModifiedDateFrom = (field: any, error: any, formState: any, values: any) => (
        <DateTimePicker
            label={t('filters.lastModifiedDateFrom')}
            value={field.value}
            maxDate={values["lastModifiedDateTo"] !== null ? values["lastModifiedDateTo"] : null}
            onChange={field.onChange}
            ampm={false}
            renderInput={(params) => (
                <TextField
                    {...params}
                    fullWidth
                    id={"lastModifiedDateFrom"}
                    error={error !== undefined}
                    helperText={error ? error.message : ""}
                />
            )}
            inputFormat={"DD/MM/YYYY HH:mm"}
        />
    )

    const renderLastModifiedDateTo = (field: any, error: any, formState: any, values: any) => (
        <DateTimePicker
            label={t('filters.lastModifiedDateTo')}
            value={field.value}
            minDate={values["lastModifiedDateFrom"] !== null ? values["lastModifiedDateFrom"] : null}
            onChange={field.onChange}
            ampm={false}
            renderInput={(params) => (
                <TextField
                    {...params}
                    fullWidth
                    id={"lastModifiedDateTo"}
                    error={error !== undefined}
                    helperText={error ? error.message : ""}
                />
            )}
            inputFormat={"DD/MM/YYYY HH:mm"}
        />
    )

    const fields = {
        status: renderStatus,
        id:renderId,
        subscriptionPlanName:renderSubscriptionPlanName,
        customerName:renderCustomerName,
        createdDateFrom: renderCreatedDateFrom,
        createdDateTo: renderCreatedDateTo,
        lastModifiedDateFrom: renderLastModifiedDateFrom,
        lastModifiedDateTo: renderLastModifiedDateTo,
        customerId:renderCustomerId,
        commercialCategory:renderCommercialCategory,
        voucherCode:renderVoucherCode
    }



    const reset = () => {
        setFilters(customDefaultValues);
        if (customerIdExt) {
            queryParams.delete('customerId');
            const newUrl = `${location.pathname}`;
            navigate(newUrl);
        }
        if(voucherCode){
            queryParams.delete('voucherCode');
            const newUrl = `${location.pathname}`;
            navigate(newUrl);
        }
    };

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);
    const noRowsOverlayComponentParams = useMemo<any>(() => {
        return {
            title: t("noReports"),
            content: t("noReportsDescription")
        };
    }, []);

    return (
        <>
            <SectionHeader title={t('listHeader.title')}
                           subtitle={t('listHeader.subTitle')}/>

            <AdvancedFilter validationObj={validationObj} fields={fields}
                            defaultValues={filterDefaultValues}
                            customDefaultValues={customDefaultValues}
                            searchInput={searchInput} setSearchInput={setSearchInput} filters={filters}
                            setFilters={setFilters} onReset={reset}  />

                <Grid container>
                    <Grid item xs={12} sx={{padding: "16px"}}>
                        <AgGridCustomTable gridOptions={gridOptions} gridRef={gridRef}
                                           noRowsOverlayComponent={noRowsOverlayComponent}
                                           onGridReady={(params) => {
                                               params.api.setServerSideDatasource(createDatasource(filters, gridRef.current!.api));
                                           }}
                        />
                    </Grid>
                </Grid>
                {selectedContract &&
                <ContractDetailModal key={selectedContract.id} open={openDetail}
                                handleClose={() => setOpenDetail(false)}
                                contract={selectedContract}
                />
            }
            
            <CustomModal open={openUploadWarning} onClose={() => setOpenUploadWarning(false)}
                         title={!importError ? t('importSuccess.title'):t('importErrors.title')} cancelButton={t('buttons.close')}
                         content={()=><p>{uploadMessage}</p>}
                         titleIcon={importError ? <WarningRounded color={"error"}/> :  <CheckCircle color={"success"} />}
            />
        </>
    );
}

export default ContractListPage;